<template>
  <div class="blog-payment-cta" :class="`cta-${variant}`">
    <div class="cta-badge" v-if="plan?.topBadge">
      <span>{{ plan.topBadge }}</span>
    </div>
    <div class="cta-content">
      <div class="cta-header">
        <img v-if="plan?.icon" :src="plan.icon" :alt="`${planName} plan`" class="plan-icon">
        <h3>{{ title || `Upgrade to ${planName}` }}</h3>
      </div>
      <p class="cta-description">{{ description || defaultDescription }}</p>
      <div class="cta-pricing-row">
        <div class="cta-price">
          <span class="price-currency">$</span>
          <span class="price-amount">{{ monthlyPrice }}</span>
          <span class="price-period">/month</span>
        </div>
        <div class="cta-savings" v-if="showSavings && yearlySavings">
          Save ${{ yearlySavings }}/year
        </div>
      </div>
      <div class="cta-features" v-if="showFeatures && displayFeatures.length">
        <div class="feature-item" v-for="feature in displayFeatures" :key="feature">
          <i class="s-icon-check"></i>
          <span>{{ feature }}</span>
        </div>
      </div>
      <s-button 
        :btn-link="`/pricing?billing_plan_id=${billingId}`"
        :class="buttonClasses"
        class="cta-button"
        :icon-font-after="buttonIcon"
      >
        {{ buttonText }}
      </s-button>
      <div class="cta-trust-elements">
        <div class="trust-item">
          <i class="s-icon-shield-check"></i>
          <span>Secure Payment</span>
        </div>
        <div class="trust-item">
          <i class="s-icon-refresh"></i>
          <span>7-Day Money Back</span>
        </div>
        <div class="trust-item">
          <i class="s-icon-users"></i>
          <span>Trusted by 1M+ users</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted } from 'vue'
import { usePlansStore } from '#root/stores/plans'
import SButton from './SButton.vue'

const props = defineProps({
  planKey: String,
  billingId: String,
  title: String,
  description: String,
  buttonText: { type: String, default: 'Get Started' },
  variant: { type: String, default: 'default' }, // default, compact, minimal
  showFeatures: { type: Boolean, default: true },
  showSavings: { type: Boolean, default: true },
  showTrust: { type: Boolean, default: true },
  buttonVariant: { type: String, default: 'primary' },
  buttonIcon: { type: String, default: 's-icon-arrow-right' }
})

// Store initialization - delayed to avoid SSR issues
let plansStore = null

// Initialize store on mount to avoid SSR issues
onMounted(() => {
  try {
    plansStore = usePlansStore()
    if (!plansStore.planInfo || Object.keys(plansStore.planInfo).length === 0) {
      console.warn('Plans store not initialized')
    }
  } catch (error) {
    console.error('Failed to initialize plans store:', error)
  }
})

const plan = computed(() => {
  if (!props.planKey || !plansStore) return null
  return plansStore?.planInfo?.[props.planKey] || null
})

const planName = computed(() => plan.value?.name || 'Premium')

const monthlyPrice = computed(() => {
  if (!props.billingId || !plansStore) return '0'
  const pricing = plansStore?.pricing?.[props.billingId]
  if (!pricing) return '0'
  const price = pricing.USD || '0'
  // Remove decimal if it's .00
  return price.replace(/\.00$/, '')
})

const defaultDescription = computed(() => {
  if (!plan.value) return 'Unlock premium features and take your experience to the next level'
  
  const features = plan.value.displayFeatures
  if (Array.isArray(features)) {
    return features.slice(0, 3).join(' • ')
  }
  
  // For plans with standard features, show some key features
  const planFeatures = plansStore?.featureOverrides?.[props.planKey]
  if (planFeatures) {
    const highlights = []
    if (planFeatures.storage) highlights.push(`${planFeatures.storage} Storage`)
    if (planFeatures.hdStreaming) highlights.push('HD Streaming')
    if (planFeatures.webdav) highlights.push('WebDAV Access')
    return highlights.slice(0, 3).join(' • ')
  }
  
  return 'Unlock premium features and take your experience to the next level'
})

const displayFeatures = computed(() => {
  if (!plan.value || !props.showFeatures) return []
  
  if (Array.isArray(plan.value.displayFeatures)) {
    return plan.value.displayFeatures.slice(0, 4)
  }
  
  // Build features from plan data
  const features = []
  if (plan.value.storage) features.push(`${plan.value.storage} Storage`)
  if (plan.value.taskSlots) features.push(`${plan.value.taskSlots} Task Slots`)
  
  const planFeatures = plansStore?.featureOverrides?.[props.planKey]
  if (planFeatures?.hdStreaming) features.push('HD/4K Streaming')
  if (planFeatures?.webdav) features.push('WebDAV & FTP Access')
  
  return features.slice(0, 4)
})

const yearlySavings = computed(() => {
  if (!plan.value || !props.showSavings) return null
  return plan.value.pricing?.savings || null
})

const buttonClasses = computed(() => {
  return [
    's-button',
    `s-button-${props.buttonVariant}`,
    's-button-large'
  ]
})
</script>